/**
 * Materials Version Control System
 * 
 * Provides semantic versioning, inheritance logic, and rollback capabilities
 * for the CDN-first materials management system.
 */

import * as Sentry from '@sentry/react';

/**
 * Semantic version parsing and operations
 */
class SemanticVersion {
  constructor(version) {
    if (typeof version === 'string') {
      const match = version.match(/^v?(\d+)\.(\d+)\.(\d+)$/);
      if (!match) {
        throw new Error(`Invalid semantic version: ${version}`);
      }
      this.major = parseInt(match[1]);
      this.minor = parseInt(match[2]);
      this.patch = parseInt(match[3]);
    } else if (typeof version === 'object' && version !== null) {
      this.major = version.major || 0;
      this.minor = version.minor || 0;
      this.patch = version.patch || 0;
    } else {
      this.major = 1;
      this.minor = 0;
      this.patch = 0;
    }
  }

  toString() {
    return `v${this.major}.${this.minor}.${this.patch}`;
  }

  incrementPatch() {
    return new SemanticVersion({
      major: this.major,
      minor: this.minor,
      patch: this.patch + 1
    });
  }

  incrementMinor() {
    return new SemanticVersion({
      major: this.major,
      minor: this.minor + 1,
      patch: 0
    });
  }

  incrementMajor() {
    return new SemanticVersion({
      major: this.major + 1,
      minor: 0,
      patch: 0
    });
  }

  compare(other) {
    if (this.major !== other.major) return this.major - other.major;
    if (this.minor !== other.minor) return this.minor - other.minor;
    return this.patch - other.patch;
  }

  isGreaterThan(other) {
    return this.compare(other) > 0;
  }

  isEqualTo(other) {
    return this.compare(other) === 0;
  }
}

/**
 * Version change analysis for determining increment type
 */
class VersionChangeAnalyzer {
  static analyzeChanges(currentFiles, newFiles) {
    const analysis = {
      filesAdded: 0,
      filesRemoved: 0,
      filesModified: 0,
      recommendedIncrementType: 'patch',
      changeDetails: []
    };

    const currentFileMap = new Map();
    const newFileMap = new Map();

    // Create maps for easier comparison
    currentFiles.forEach(file => {
      currentFileMap.set(file.original_filename, file);
    });

    newFiles.forEach(file => {
      newFileMap.set(file.original_filename, file);
    });

    // Find added files
    newFiles.forEach(newFile => {
      if (!currentFileMap.has(newFile.original_filename)) {
        analysis.filesAdded++;
        analysis.changeDetails.push({
          type: 'added',
          filename: newFile.original_filename,
          size: newFile.file_size_bytes
        });
      }
    });

    // Find removed and modified files
    currentFiles.forEach(currentFile => {
      if (!newFileMap.has(currentFile.original_filename)) {
        analysis.filesRemoved++;
        analysis.changeDetails.push({
          type: 'removed',
          filename: currentFile.original_filename
        });
      } else {
        const newFile = newFileMap.get(currentFile.original_filename);
        if (currentFile.file_size_bytes !== newFile.file_size_bytes) {
          analysis.filesModified++;
          analysis.changeDetails.push({
            type: 'modified',
            filename: currentFile.original_filename,
            oldSize: currentFile.file_size_bytes,
            newSize: newFile.file_size_bytes
          });
        }
      }
    });

    // Determine recommended increment type
    if (analysis.filesRemoved > 0) {
      analysis.recommendedIncrementType = 'major';
    } else if (analysis.filesAdded > 0) {
      analysis.recommendedIncrementType = 'minor';
    } else if (analysis.filesModified > 0) {
      analysis.recommendedIncrementType = 'patch';
    }

    return analysis;
  }
}

/**
 * Version inheritance logic for new SOs vs existing SOs
 */
class VersionInheritanceManager {
  static async determineVersionForSO(sessionId, soId, isNewSO = true) {
    try {
      if (isNewSO) {
        // New SOs get the latest master version
        return await this.getLatestMasterVersion(sessionId);
      } else {
        // Existing SOs keep their locked version
        return await this.getLockedVersionForSO(sessionId, soId);
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { operation: 'version_inheritance' },
        extra: { sessionId, soId, isNewSO }
      });
      throw error;
    }
  }

  static async getLatestMasterVersion(sessionId) {
    const { getLatestMasterVersion } = await import('./materials-airtable.js');
    return getLatestMasterVersion(sessionId);
  }

  static async getLockedVersionForSO(sessionId, soId) {
    const { getLockedVersionForSO } = await import('./materials-airtable.js');
    return getLockedVersionForSO(sessionId, soId);
  }

  static async lockVersionForSO(soId, versionId, sessionId) {
    const { lockVersionForSO } = await import('./materials-airtable.js');
    return lockVersionForSO(soId, versionId, sessionId);
  }
}

/**
 * Rollback impact analysis
 */
class RollbackImpactAnalyzer {
  static async analyzeRollbackImpact(sessionId, targetVersionId) {
    try {
      const impact = {
        affectedSOs: [],
        filesAffected: 0,
        potentialIssues: [],
        canRollback: true,
        recommendedActions: []
      };

      // Get all SOs using versions newer than target
      const affectedSOs = await this.getSOsAffectedByRollback(sessionId, targetVersionId);
      impact.affectedSOs = affectedSOs;

      // Analyze potential issues
      if (affectedSOs.length > 0) {
        impact.potentialIssues.push({
          type: 'version_mismatch',
          message: `${affectedSOs.length} SOs are using newer versions and will be unaffected`,
          severity: 'info'
        });
      }

      // Check if target version is still available
      const targetVersionExists = await this.verifyVersionExists(targetVersionId);
      if (!targetVersionExists) {
        impact.canRollback = false;
        impact.potentialIssues.push({
          type: 'version_missing',
          message: 'Target version no longer exists in storage',
          severity: 'error'
        });
      }

      return impact;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { operation: 'rollback_analysis' },
        extra: { sessionId, targetVersionId }
      });
      throw error;
    }
  }

  static async getSOsAffectedByRollback(sessionId, targetVersionId) {
    const { getSOsAffectedByRollback } = await import('./materials-airtable.js');
    return getSOsAffectedByRollback(sessionId, targetVersionId);
  }

  static async verifyVersionExists(versionId) {
    const { verifyVersionExists } = await import('./materials-airtable.js');
    return verifyVersionExists(versionId);
  }
}

/**
 * Version comparison utilities
 */
class VersionComparator {
  static async compareVersions(versionId1, versionId2) {
    try {
      const comparison = {
        version1: null,
        version2: null,
        filesOnlyInVersion1: [],
        filesOnlyInVersion2: [],
        filesModified: [],
        identical: false
      };

      // Get file manifests for both versions
      const [version1Files, version2Files] = await Promise.all([
        this.getVersionFiles(versionId1),
        this.getVersionFiles(versionId2)
      ]);

      comparison.version1 = { id: versionId1, fileCount: version1Files.length };
      comparison.version2 = { id: versionId2, fileCount: version2Files.length };

      // Create maps for comparison
      const v1Map = new Map();
      const v2Map = new Map();

      version1Files.forEach(file => v1Map.set(file.original_filename, file));
      version2Files.forEach(file => v2Map.set(file.original_filename, file));

      // Find files only in version 1
      version1Files.forEach(file => {
        if (!v2Map.has(file.original_filename)) {
          comparison.filesOnlyInVersion1.push({
            filename: file.original_filename,
            size: file.file_size_bytes,
            contentType: file.content_type
          });
        }
      });

      // Find files only in version 2 or modified files
      version2Files.forEach(file => {
        if (!v1Map.has(file.original_filename)) {
          comparison.filesOnlyInVersion2.push({
            filename: file.original_filename,
            size: file.file_size_bytes,
            contentType: file.content_type
          });
        } else {
          const v1File = v1Map.get(file.original_filename);
          if (v1File.file_size_bytes !== file.file_size_bytes) {
            comparison.filesModified.push({
              filename: file.original_filename,
              version1Size: v1File.file_size_bytes,
              version2Size: file.file_size_bytes,
              sizeDiff: file.file_size_bytes - v1File.file_size_bytes
            });
          }
        }
      });

      // Check if versions are identical
      comparison.identical = 
        comparison.filesOnlyInVersion1.length === 0 &&
        comparison.filesOnlyInVersion2.length === 0 &&
        comparison.filesModified.length === 0;

      return comparison;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { operation: 'version_comparison' },
        extra: { versionId1, versionId2 }
      });
      throw error;
    }
  }

  static async getVersionFiles(versionId) {
    const { getVersionFiles } = await import('./materials-airtable.js');
    return getVersionFiles(versionId);
  }
}

/**
 * Version usage analytics
 */
class VersionUsageAnalytics {
  static async getVersionUsageStats(sessionId, timeRange = '30days') {
    try {
      const stats = {
        totalVersions: 0,
        activeVersions: 0,
        versionDistribution: {},
        usageByVersion: {},
        topDownloadedFiles: [],
        timeRange
      };

      // Get all versions for the session
      const versions = await this.getVersionsForSession(sessionId);
      stats.totalVersions = versions.length;

      // Calculate version distribution
      for (const version of versions) {
        const usageCount = await this.getVersionUsageCount(version.version_id);
        stats.versionDistribution[version.version_id] = usageCount;
        
        if (usageCount > 0) {
          stats.activeVersions++;
        }

        // Get usage analytics for this version
        const usage = await this.getVersionUsageAnalytics(version.version_id, timeRange);
        stats.usageByVersion[version.version_id] = usage;
      }

      // Get top downloaded files across all versions
      stats.topDownloadedFiles = await this.getTopDownloadedFiles(sessionId, timeRange, 10);

      return stats;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { operation: 'usage_analytics' },
        extra: { sessionId, timeRange }
      });
      throw error;
    }
  }

  static async getVersionsForSession(sessionId) {
    const { fetchVersionsForSession } = await import('./materials-airtable.js');
    return fetchVersionsForSession(sessionId);
  }

  static async getVersionUsageCount(versionId) {
    const { getVersionUsageCount } = await import('./materials-airtable.js');
    return getVersionUsageCount(versionId);
  }

  static async getVersionUsageAnalytics(versionId, timeRange) {
    // For now, return basic usage count - can be expanded later
    const usageCount = await this.getVersionUsageCount(versionId);
    return {
      totalDownloads: usageCount,
      timeRange,
      versionId
    };
  }

  static async getTopDownloadedFiles(sessionId, timeRange, limit) {
    const { getTopDownloadedFiles } = await import('./materials-airtable.js');
    return getTopDownloadedFiles(sessionId, timeRange, limit);
  }
}

/**
 * Bulk Operations Manager for handling multiple session updates
 */
class BulkOperationsManager {
  static async performBulkUpdate(selectedSessions, files, operationData, adminUser) {
    try {
      // Create bulk operation record
      const operation = await this.createBulkOperation('Bulk Upload', selectedSessions, operationData, adminUser);
      
      // Process each session with version control
      const results = {
        operationId: operation.operation_id,
        affectedSessions: [],
        versionsCreated: [],
        totalFilesProcessed: files.length,
        errors: []
      };

      // Analyze changes for each session
      for (const sessionId of selectedSessions) {
        try {
          // Get current master version for this session
          const currentVersion = await VersionInheritanceManager.getLatestMasterVersion(sessionId);
          const currentFiles = currentVersion ? await VersionComparator.getVersionFiles(currentVersion.version_id) : [];

          // Analyze changes
          const changeAnalysis = VersionChangeAnalyzer.analyzeChanges(currentFiles, files);

          // Create new version using standard version control
          const versionResult = await MaterialsVersionControl.createNewVersion(
            sessionId,
            files,
            operationData.changeNotes,
            adminUser
          );

          results.versionsCreated.push({
            sessionId,
            versionId: versionResult.versionId,
            filesCount: files.length,
            changeAnalysis: versionResult.changeAnalysis,
            success: true
          });

          results.affectedSessions.push(sessionId);

          // Update operation progress
          await this.updateOperationProgress(operation.operation_id, {
            sessionsCompleted: results.affectedSessions.length,
            overallProgress: (results.affectedSessions.length / selectedSessions.length) * 100
          });

        } catch (error) {
          console.error(`Failed to update session ${sessionId}:`, error);
          results.errors.push({
            sessionId,
            error: error.message
          });
          
          Sentry.captureException(error, {
            tags: { operation: 'bulk_update_session' },
            extra: { sessionId, operationId: operation.operation_id }
          });
        }
      }

      // Complete the bulk operation
      await this.completeOperation(operation.operation_id, results);

      return results;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { operation: 'bulk_update' },
        extra: { selectedSessions, adminUser }
      });
      throw error;
    }
  }

  static async performBulkRollback(selectedSessions, targetVersions, adminUser, rollbackReason) {
    try {
      const operation = await this.createBulkOperation('Bulk Rollback', selectedSessions, { rollbackReason }, adminUser);
      
      const results = {
        operationId: operation.operation_id,
        rolledBackSessions: [],
        errors: []
      };

      for (let i = 0; i < selectedSessions.length; i++) {
        const sessionId = selectedSessions[i];
        const targetVersionId = targetVersions[i] || targetVersions[0]; // Use first version if not enough specified

        try {
          // Analyze rollback impact
          const impact = await RollbackImpactAnalyzer.analyzeRollbackImpact(sessionId, targetVersionId);
          
          if (!impact.canRollback) {
            throw new Error(`Cannot rollback session ${sessionId}: ${impact.potentialIssues.map(i => i.message).join(', ')}`);
          }

          // Perform rollback using standard version control
          const rollbackResult = await MaterialsVersionControl.rollbackToVersion(sessionId, targetVersionId, adminUser);
          
          results.rolledBackSessions.push({
            sessionId,
            targetVersionId,
            success: rollbackResult.success
          });

          // Update progress
          await this.updateOperationProgress(operation.operation_id, {
            sessionsCompleted: results.rolledBackSessions.length,
            overallProgress: (results.rolledBackSessions.length / selectedSessions.length) * 100
          });

        } catch (error) {
          console.error(`Failed to rollback session ${sessionId}:`, error);
          results.errors.push({
            sessionId,
            error: error.message
          });
        }
      }

      await this.completeOperation(operation.operation_id, results);
      return results;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { operation: 'bulk_rollback' },
        extra: { selectedSessions, adminUser }
      });
      throw error;
    }
  }

  static async createBulkOperation(operationType, affectedSessions, operationData, adminUser) {
    const { createBulkOperationRecord } = await import('./materials-airtable.js');
    return createBulkOperationRecord(operationType, affectedSessions, operationData, adminUser);
  }

  static async updateOperationProgress(operationId, progressData) {
    const { updateBulkOperationProgress } = await import('./materials-airtable.js');
    return updateBulkOperationProgress(operationId, progressData);
  }

  static async completeOperation(operationId, results) {
    const { updateBulkOperationProgress } = await import('./materials-airtable.js');
    return updateBulkOperationProgress(operationId, {
      overallProgress: 100,
      completed: true,
      success: results.errors.length === 0,
      sessionsCompleted: results.affectedSessions?.length || results.rolledBackSessions?.length || 0,
      errorMessage: results.errors.length > 0 ? `${results.errors.length} operations failed` : null
    });
  }

  static async getBulkOperationsHistory() {
    const { getBulkOperationsHistory } = await import('./materials-airtable.js');
    return getBulkOperationsHistory();
  }
}

/**
 * Main version control orchestrator
 */
class MaterialsVersionControl {
  static async createNewVersion(sessionId, newFiles, changeNotes, adminUser) {
    try {
      // Get current master version
      const currentVersion = await VersionInheritanceManager.getLatestMasterVersion(sessionId);
      const currentFiles = currentVersion ? await VersionComparator.getVersionFiles(currentVersion.version_id) : [];

      // Analyze changes
      const changeAnalysis = VersionChangeAnalyzer.analyzeChanges(currentFiles, newFiles);

      // Determine new version number
      const currentSemVer = currentVersion ? new SemanticVersion(currentVersion.version_id) : new SemanticVersion('v0.0.0');
      let newVersion;

      switch (changeAnalysis.recommendedIncrementType) {
        case 'major':
          newVersion = currentSemVer.incrementMajor();
          break;
        case 'minor':
          newVersion = currentSemVer.incrementMinor();
          break;
        default:
          newVersion = currentSemVer.incrementPatch();
      }

      // Create version record in Airtable
      const versionRecord = await this.createVersionRecord(sessionId, newVersion.toString(), newFiles, changeNotes, adminUser, changeAnalysis);

      return {
        versionId: newVersion.toString(),
        record: versionRecord,
        changeAnalysis
      };
    } catch (error) {
      Sentry.captureException(error, {
        tags: { operation: 'create_version' },
        extra: { sessionId, changeNotes, adminUser }
      });
      throw error;
    }
  }

  static async rollbackToVersion(sessionId, targetVersionId, adminUser) {
    try {
      // Analyze rollback impact
      const impact = await RollbackImpactAnalyzer.analyzeRollbackImpact(sessionId, targetVersionId);

      if (!impact.canRollback) {
        throw new Error(`Cannot rollback to version ${targetVersionId}: ${impact.potentialIssues.map(i => i.message).join(', ')}`);
      }

      // Perform rollback
      const rollbackResult = await this.performRollback(sessionId, targetVersionId, adminUser, impact);

      return {
        success: true,
        targetVersion: targetVersionId,
        impact,
        rollbackResult
      };
    } catch (error) {
      Sentry.captureException(error, {
        tags: { operation: 'rollback_version' },
        extra: { sessionId, targetVersionId, adminUser }
      });
      throw error;
    }
  }

  static async createVersionRecord(sessionId, versionId, files, changeNotes, adminUser, changeAnalysis) {
    const { createVersionRecord } = await import('./materials-airtable.js');
    return createVersionRecord(sessionId, versionId, files, changeNotes, adminUser, changeAnalysis);
  }

  static async performRollback(sessionId, targetVersionId, adminUser, impact) {
    const { performRollback } = await import('./materials-airtable.js');
    return performRollback(sessionId, targetVersionId, adminUser, impact);
  }
}

// Export all classes for use in other modules
export {
  SemanticVersion,
  VersionChangeAnalyzer,
  VersionInheritanceManager,
  RollbackImpactAnalyzer,
  VersionComparator,
  VersionUsageAnalytics,
  BulkOperationsManager,
  MaterialsVersionControl
};